import moment, { unitOfTime } from 'moment';

interface IFormatServerTime {
  date: string;
  includeTime?: boolean;
  parseFormat?: string;
  format?: string;
}

export const serverToUnix = (ds: string) => {
  return moment(ds, 'MM/DD/YYYY hh:mm A').unix();
};

export const daysBetween = (future: Date, past?: Date) => {
  return moment(future).diff(past ? moment(past) : moment(), 'days');
};

export const formatServerTime = ({
  date,
  includeTime = false,
  parseFormat = 'MM/DD/YYYY hh:mm A',
  format = '',
}: IFormatServerTime) => {
  if (!date) {
    return null;
  }
  const _format =
    format || (includeTime ? 'DD MMM YYYY, hh:mm a' : 'DD MMM YYYY');
  return moment(date, parseFormat).format(_format);
};

export const formatDateObj = (date?: Date, format = 'MM/DD/YYYY') => {
  if (!date) {
    return '';
  }
  return moment(date).format(format);
};

export const formatDate = (
  date?: moment.Moment | null,
  format = 'DD MMM YYYY, hh:mm a'
) => {
  let _date = date;
  if (!_date) {
    _date = moment();
  }
  return _date.format(format);
};

export const parseDate = (date_str: string, format: string) => {
  return moment(date_str, format);
};

export const parseAndFormat = (
  datestr: string,
  parseFormat = 'YYYY-MM-DD',
  format = 'LL'
) => {
  if (!datestr) {
    return '';
  }

  return moment(datestr, parseFormat).format(format);
};

export const parseEpochAndFormat = (
  date: number,
  format = 'hh:mm a, DD MMM YYYY'
) => {
  return moment.unix(date).format(format);
};

export const humanReadableDuration = (duration: number) => {
  if (duration < 60) {
    return `${duration} seconds`;
  }
  // @ts-ignore
  const mins = parseFloat(duration / 60).toFixed(1);
  return `${mins} mins`;
};

export const serverTimeToDate = (
  date: string,
  parseFormat = 'YYYY-MM-DDThh:mm:ssZ',
  fallback = new Date()
) => {
  if (!date) {
    return fallback;
  }
  return moment(date, parseFormat).toDate();
};

export const epochToDate = (date: number) => {
  return moment.unix(date).toDate();
};

export const todayDate = () => {
  return moment().format('DD MMM YYYY');
};

export const tomorrowDate = () => {
  return moment().add(1, 'day').format('DD MMM YYYY');
};

export const xDaysLater = (x: number) => {
  return moment().add(x, 'days').toDate();
};

export const xDaysAgo = (x: number) => {
  return moment().subtract(x, 'days').toDate();
};

export const now = () => {
  return moment().toDate();
};

export const epochNow = () => {
  return moment().unix();
};

export const setHours = (hour: number) => {
  return moment().set('hour', hour).set('minute', 0).toDate();
};

export const startOfHour = (delta: number) => {
  return moment().add(delta, 'hours').startOf('hour').toDate();
};

export const startOf = (unit: unitOfTime.DurationConstructor) =>
  moment().startOf(unit).toDate();

export const endOf = (unit: unitOfTime.DurationConstructor) =>
  moment().endOf(unit).toDate();

export const startOfDay = (time: Date) => moment(time).startOf('day');

export const endOfDay = (time: Date) => moment(time).endOf('day');

export const momentOfTime = (time?: Date | string) => moment(time);

export const minutesDiff = (start: moment.Moment, end: moment.Moment) =>
  moment.duration(end.diff(start)).asMinutes();

export const timeStringToDate = (timeString: string): Date => {
  if (!timeString) {
    return new Date();
  }

  // Parse time string in HH:mm format (e.g., "15:45")
  const [hours, minutes] = timeString.split(':').map(Number);

  // Create a new Date object with today's date and the specified time
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date;
};

export const parseTimeString = (timeString: string): Date => {
  if (!timeString) {
    return new Date();
  }

  // Parse time string in format like "7:00 AM", "8:00 PM"
  const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
  const match = timeString.match(timeRegex);

  if (!match) {
    console.warn(
      `Invalid time format: ${timeString}. Expected format: "7:00 AM" or "8:00 PM"`
    );
    return new Date();
  }

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  // Convert to 24-hour format
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  // Create a new Date object with today's date and the specified time
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date;
};
